import type {
  AppBadgeCountModeT,
  FavoriteShortcut,
  PriorityColorMap,
  ProjectSidebarCountT,
  ProjectSidebarSortT,
  TagColorMap,
  TaskSwipeActionT,
  UiPreferences,
} from '@portfolio-pm/shared';
import {
  AppBadgeCountModeSchema,
  DEFAULT_PRIORITY_COLORS,
  DEFAULT_TASK_SWIPE_ACTIONS,
  ProjectSidebarCountSchema,
  ProjectSidebarSortSchema,
  TaskSwipeActionSchema,
  UiPreferencesSchema,
} from '@portfolio-pm/shared';
import type * as Y from 'yjs';
import { type WithNow, ensureUiPreferences, getUiPreferencesMap } from './doc.ts';

export type UpdateUiPreferencesInput = Partial<
  Pick<
    UiPreferences,
    | 'priorityColors'
    | 'tagColors'
    | 'taskSwipeLeftToRight'
    | 'taskSwipeRightToLeft'
    | 'favoriteShortcuts'
    | 'projectSidebarSort'
    | 'projectSidebarCount'
    | 'projectSidebarShowArchived'
    | 'appBadgeCountMode'
  >
>;

function ts(options: WithNow): number {
  return options.now ?? Date.now();
}

function normalizePriorityColors(value: unknown): PriorityColorMap {
  if (!value || typeof value !== 'object') return { ...DEFAULT_PRIORITY_COLORS };
  return {
    ...DEFAULT_PRIORITY_COLORS,
    ...(value as Partial<Record<keyof PriorityColorMap, string>>),
  };
}

function normalizeTagColors(value: unknown): TagColorMap {
  if (!value || typeof value !== 'object') return {};
  const colors: TagColorMap = {};
  for (const [tag, color] of Object.entries(value as Record<string, unknown>)) {
    if (tag.trim() && typeof color === 'string' && color.trim()) colors[tag] = color;
  }
  return colors;
}

function normalizeTaskSwipeAction(value: unknown, fallback: TaskSwipeActionT): TaskSwipeActionT {
  const parsed = TaskSwipeActionSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

function normalizeFavoriteShortcuts(value: unknown): FavoriteShortcut[] {
  if (!Array.isArray(value)) return [];
  const out: FavoriteShortcut[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const candidate = item as Record<string, unknown>;
    const kind = candidate.kind;
    const id = candidate.id;
    if ((kind !== 'project' && kind !== 'smart') || typeof id !== 'string' || !id.trim()) continue;
    const key = `${kind}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind, id });
  }
  return out;
}

function normalizeProjectSidebarSort(value: unknown): ProjectSidebarSortT {
  const parsed = ProjectSidebarSortSchema.safeParse(value);
  return parsed.success ? parsed.data : 'alphabetical';
}

function normalizeProjectSidebarCount(value: unknown): ProjectSidebarCountT {
  const parsed = ProjectSidebarCountSchema.safeParse(value);
  return parsed.success ? parsed.data : 'active';
}

function normalizeAppBadgeCountMode(value: unknown): AppBadgeCountModeT {
  const parsed = AppBadgeCountModeSchema.safeParse(value);
  return parsed.success ? parsed.data : 'today';
}

export function getUiPreferences(doc: Y.Doc): UiPreferences {
  ensureUiPreferences(doc);
  const map = getUiPreferencesMap(doc);
  return UiPreferencesSchema.parse({
    priorityColors: normalizePriorityColors(map.get('priorityColors')),
    tagColors: normalizeTagColors(map.get('tagColors')),
    taskSwipeLeftToRight: normalizeTaskSwipeAction(
      map.get('taskSwipeLeftToRight'),
      DEFAULT_TASK_SWIPE_ACTIONS.leftToRight,
    ),
    taskSwipeRightToLeft: normalizeTaskSwipeAction(
      map.get('taskSwipeRightToLeft'),
      DEFAULT_TASK_SWIPE_ACTIONS.rightToLeft,
    ),
    favoriteShortcuts: normalizeFavoriteShortcuts(map.get('favoriteShortcuts')),
    projectSidebarSort: normalizeProjectSidebarSort(map.get('projectSidebarSort')),
    projectSidebarCount: normalizeProjectSidebarCount(map.get('projectSidebarCount')),
    projectSidebarShowArchived: map.get('projectSidebarShowArchived') === true,
    appBadgeCountMode: normalizeAppBadgeCountMode(map.get('appBadgeCountMode')),
    createdAt: Number(map.get('createdAt') ?? 0),
    updatedAt: Number(map.get('updatedAt') ?? 0),
  });
}

export function updateUiPreferences(
  doc: Y.Doc,
  patch: UpdateUiPreferencesInput,
  options: WithNow = {},
): UiPreferences {
  const now = ts(options);
  ensureUiPreferences(doc, now);
  const map = getUiPreferencesMap(doc);

  doc.transact(() => {
    if (patch.priorityColors !== undefined) {
      map.set('priorityColors', normalizePriorityColors(patch.priorityColors));
    }
    if (patch.tagColors !== undefined) {
      map.set('tagColors', normalizeTagColors(patch.tagColors));
    }
    if (patch.taskSwipeLeftToRight !== undefined) {
      map.set(
        'taskSwipeLeftToRight',
        normalizeTaskSwipeAction(
          patch.taskSwipeLeftToRight,
          DEFAULT_TASK_SWIPE_ACTIONS.leftToRight,
        ),
      );
    }
    if (patch.taskSwipeRightToLeft !== undefined) {
      map.set(
        'taskSwipeRightToLeft',
        normalizeTaskSwipeAction(
          patch.taskSwipeRightToLeft,
          DEFAULT_TASK_SWIPE_ACTIONS.rightToLeft,
        ),
      );
    }
    if (patch.favoriteShortcuts !== undefined) {
      map.set('favoriteShortcuts', normalizeFavoriteShortcuts(patch.favoriteShortcuts));
    }
    if (patch.projectSidebarSort !== undefined) {
      map.set('projectSidebarSort', normalizeProjectSidebarSort(patch.projectSidebarSort));
    }
    if (patch.projectSidebarCount !== undefined) {
      map.set('projectSidebarCount', normalizeProjectSidebarCount(patch.projectSidebarCount));
    }
    if (patch.projectSidebarShowArchived !== undefined) {
      map.set('projectSidebarShowArchived', patch.projectSidebarShowArchived === true);
    }
    if (patch.appBadgeCountMode !== undefined) {
      map.set('appBadgeCountMode', normalizeAppBadgeCountMode(patch.appBadgeCountMode));
    }
    map.set('updatedAt', now);
  });

  return getUiPreferences(doc);
}
